import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

export const SITE_URL = "https://evangelizaciondigitalhuelva.org";
export const SITE_NAME = "Evangelización Digital | Diócesis de Huelva";
const DEFAULT_DESCRIPTION =
  "Delegación de Evangelización Digital de la Diócesis de Huelva. Llevamos el Evangelio al mundo digital.";
// Tarjeta 1200×630 generada a partir del logotipo. Antes esto apuntaba al
// icono suelto, que en una tarjeta de Facebook o WhatsApp salía diminuto y
// descentrado sobre una franja blanca.
const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;

// Metadatos por página.
//
// Hasta ahora react-helmet-async solo se usaba en el detalle de noticia, así
// que las otras doce páginas públicas compartían el título y la descripción
// del index.html. Buscar "formación evangelización digital Huelva" devolvía la
// misma ficha genérica para todas.
//
// `title` se compone con el nombre del sitio salvo que se pase `bare`, que lo
// deja tal cual (útil en la portada, donde el título ya es el del sitio).
export default function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_IMAGE,
  type = "website",
  canonical,
  noIndex = false,
  bare = false,
  children,
}) {
  const { pathname } = useLocation();
  const url = canonical || `${SITE_URL}${pathname}`;
  const fullTitle = !title ? SITE_NAME : bare ? title : `${title} | ${SITE_NAME}`;

  // index.html trae su propio juego de etiquetas SEO, marcadas con
  // `data-default`. Sirven de respaldo para los rastreadores que no ejecutan
  // JavaScript, así que deben seguir en el fichero.
  //
  // El problema es que Helmet **añade** las suyas al final del head en lugar de
  // sustituirlas, de modo que en el navegador acababa habiendo dos
  // `description`, dos `canonical` y hasta dos `<title>`. Ante dos canonical
  // distintos, Google descarta ambos.
  //
  // En cuanto Helmet ha puesto las de la página se retiran las de respaldo.
  // Se hace en un temporizador a cero para caer después del volcado al DOM de
  // Helmet, que no ocurre dentro del propio efecto.
  useEffect(() => {
    const id = setTimeout(() => {
      document.head.querySelectorAll("[data-default]").forEach(el => el.remove());
    }, 0);
    return () => clearTimeout(id);
  }, []);

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noIndex && <meta name="robots" content="noindex, follow" />}

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content="es_ES" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      {children}
    </Helmet>
  );
}
